import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import parse from "html-react-parser";
import dayjs from 'dayjs';

// Prime React Components
import { classNames } from 'primereact/utils';
import { FilterMatchMode } from 'primereact/api';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import { Calendar } from 'primereact/calendar';
import { InputTextarea } from 'primereact/inputtextarea';
import { Checkbox } from 'primereact/checkbox';
import { CascadeSelect } from 'primereact/cascadeselect';
import { Dropdown } from 'primereact/dropdown';
import { SelectButton } from 'primereact/selectbutton';
import { FileUpload } from 'primereact/fileupload';
import { RadioButton } from 'primereact/radiobutton';



// Needed URLS
import { GET_NEURELIS_RESOURCES, ADD_NEURELIS_RESOURCE, EDIT_NEURELIS_RESOURCE, DELETE_NEURELIS_RESOURCE, GET_RESOURCE_CATEGORIES, 
	NEURELIS_IMAGE_PATH, NEURELIS_PDF_PATH } from '../constants/URLS';

// Needed Components
import FileTypeChooser from '../components/fileTypeChooser'

const ResourceTable = (props) => {

	// PAGE REFS
	const toast = useRef(null);
    const dt = useRef(null);
	const fileUploadRef = useRef(null);
	const photoUploadRef = useRef(null);
	const curYear =new Date().getFullYear()

	// CONSTANTS
    const emptyResource = {
        resourceID: null,
        title: '',
		short_title: '',
		catSubCat: null,
		category: '',
		subcategory: '',
		subcategoryYear: curYear,
		locked: 1,
		source: '',
		path: '', 
		url: '',
		isActive: 1,
		fileType: 'file'
    };

	const lockedStatus = [
		{name: 'Not Locked', value: 0},
		{name: 'Locked', value: 1},
		{name: 'Form Required', value: 2}
	]

	// STATE VARIABLES
    const [ resources, setResources ] = useState([]); // holds all the existing resources
	const [ resource, setResource ] = useState(emptyResource); // current resource that we are adding or editing
	const [ categories, setCategories ] = useState([]); // holds all the possible categories for the dropdown
	const [ postersSubcategories, setPostersSubcategories ] = useState([]); // holds all the possible subcategories for posters
    const [ resourceDialog, setResourceDialog ] = useState(false);
    const [ deleteResourceDialog, setDeleteResourceDialog ] = useState(false);
    const [ submitted, setSubmitted ] = useState(false);
    const [ globalFilterValue1, setGlobalFilterValue1 ] = useState('');
	const [ filters, setFilters ] = useState({
        'global': { value: null, matchMode: FilterMatchMode.CONTAINS },
		'category': { value: null, matchMode: FilterMatchMode.EQUALS },
		'short_title': { value: null, matchMode: FilterMatchMode.CONTAINS },
		
    });
	const [filterCategories, setFilterCategories] = useState([]);
	const [categoryLookup, setCategoryLookup] = useState({});

	const pastYears = curYear - 3
	const subcatYears = []

	for (let _year = pastYears; _year < curYear + 3; _year++) subcatYears.push(_year)
    
	// USE EFFECTS HOOKS
    useEffect(() => {
		fncGetCategories()
    }, []); 

	useEffect(() => {
		fncGetResources()
    }, [categoryLookup]); 

	// ACTION / XHR FUNCTIONS
	const fncGetResources = async (data) => {
		let strURL = GET_NEURELIS_RESOURCES
		
		let resourcesXHR = await axios({method: 'get', url: strURL})
		// need to do some slight data modifications for the form
		if (resourcesXHR.data.success) setResources(resourcesXHR.data.results.map(cur => {
			return {
				...cur,
				lastModified: dayjs(cur.timeStamp).format('YYYYMMDD')
			}
		}))
		else {
			toast.current.show({ severity: 'error', summary: 'Error', detail: resourcesXHR.data.message, life: 3000 });
		}
	};
	
	// Set up the categories and possible the subcategories
	const fncGetCategories = async (data) => {
		let strURL = GET_RESOURCE_CATEGORIES
		
		let categoriesXHR = await axios({method: 'get', url: strURL})
		if (categoriesXHR.data.success) {
			let _categories = Object.keys(categoriesXHR.data.results).map(cur => {
				return {
					label: categoriesXHR.data.results[cur].display, 
					value: cur
				}
			})
			
			setCategories(_categories)
			setFilterCategories(_categories)

			if (categoriesXHR.data.results['posters'] && categoriesXHR.data.results['posters'].subcategories) {
				let _subcats = categoriesXHR.data.results['posters'].subcategories.map(cur => {
					return {
						label: cur,
						value: cur
					}
				})
				_subcats = _subcats.sort((a,b) => (a.label < b.label?-1:1))
				setPostersSubcategories(_subcats)
			}
		}
		else {
			toast.current.show({ severity: 'error', summary: 'Error', detail: categoriesXHR.data.message, life: 3000 });
		}
	};

	

	const saveResource = async () => {
        setSubmitted(true);
		
		let objCurUploader = fileUploadRef.current
		let arrFiles = objCurUploader.getFiles() || []
		
		let errors = []
		if (!resource.title.trim()) errors.push('title')
		if (!resource.short_title.trim()) errors.push('short_title')
		if (!resource.category) errors.push('category')

		if (errors.length > 0) {
			return  toast.current.show({ severity: 'error', summary: 'Error', detail: 'Please fill in all required fields.', life: 3000 });
		}

		if (resource.fileType === 'url' && resource.url.trim() === '') {
			return  toast.current.show({ severity: 'error', summary: 'Error', detail: 'You must provide a PDF file or a valid URL.', life: 3000 });
		}
		
		if (!resource.resourceID && resource.fileType === 'file' && arrFiles.length === 0) {
			return  toast.current.show({ severity: 'error', summary: 'Error', detail: 'You must provide a PDF file or a valid URL.', life: 3000 });
		}

		let _entity = {...resource};
		const formData = new FormData();
		formData.append("category", _entity.category);
		if (_entity.subcategory) {
			formData.append("subcategory", _entity.subcategory);
			formData.append("subcategoryYear", _entity.subcategoryYear);
		}
		
		formData.append("description", _entity.description);
		formData.append("fileType", _entity.fileType);
		formData.append("infographic", _entity.infographic);
		formData.append("isActive", _entity.isActive);
		formData.append("locked", _entity.locked);
		formData.append("path", _entity.path);
		formData.append("resourceID", _entity.resourceID);
		formData.append("retired", _entity.retired);
		formData.append("short_title", _entity.short_title);
		formData.append("source", _entity.source);
		formData.append("title", _entity.title);
		formData.append("url", _entity.url);

		
		if (arrFiles[0]) {
			formData.append("file", arrFiles[0]);
		}

		let objCurPhotoUploader = photoUploadRef.current
		let arrPhoto= objCurPhotoUploader.getFiles() || []
		if (arrPhoto[0]) {
			formData.append("photo", arrPhoto[0]);
		}

		if (_entity.resourceID) {
			
			let editResults = await axios({
				method: 'post', 
				url: EDIT_NEURELIS_RESOURCE,
				data: formData,
				headers: { "Content-Type": "multipart/form-data" }
			})

			if (editResults.data.success) {
				await fncGetResources()
				toast.current.show({ severity: 'success', summary: 'Successful', detail: 'Resource Updated', life: 3000 });
			} else {
				toast.current.show({ severity: 'error', summary: 'Error', detail: editResults.data.message, life: 3000 });
			}
		}
		else {
			let addResults = await axios({
				method: 'post', 
				url: ADD_NEURELIS_RESOURCE,
				data: formData,
				headers: { "Content-Type": "multipart/form-data" }
			})
			
			if (addResults.data.success) {
				await fncGetResources()
				toast.current.show({ severity: 'success', summary: 'Successful', detail: 'Resource Created', life: 3000 });
			} else {
				toast.current.show({ severity: 'error', summary: 'Error', detail: addResults.data.message, life: 3000 });
			}
		}
		
		setSubmitted(false);
		setResourceDialog(false);
		clearResource()
        
    }

	const deleteResource = async () => {
        try {
			let deleteResults = await axios({
				method: 'post', 
				url: DELETE_NEURELIS_RESOURCE,
				data: {...resource}
			})
			if (deleteResults.data.success) {
				let _resources = resources.filter(val => val.resourceID !== resource.resourceID);
				setResources(_resources);
				setDeleteResourceDialog(false);
				clearResource()
				toast.current.show({ severity: 'success', summary: 'Successful', detail: 'Resource Deleted', life: 3000 });
			} else {
				toast.current.show({ severity: 'error', summary: 'Error', detail: deleteResults.data.message, life: 3000 });
			}
		} catch (err) {
			toast.current.show({ severity: 'error', summary: 'Error', detail: 'There was an error deleting.', life: 3000 });
			console.error(err)
		}
    }
	
	// OTHER FUNCTIONS
	const clearResource = () => {
		setResource(emptyResource); 
	}

    const openNew = () => {
       clearResource()
        setSubmitted(false);
        setResourceDialog(true);
    }

    const hideDialog = () => {
        setSubmitted(false);
        setResourceDialog(false);
    }

    const hideDeleteResourceDialog = () => {
        setDeleteResourceDialog(false);
    }

    const editResource = (resource) => {
		setResource(resource);
        setResourceDialog(true);
    }

    const confirmDeleteResource = (resource) => {
        setResource(resource);
        setDeleteResourceDialog(true);
    }


    const onInputChange = (value, name) => {
       let _resource = {...resource};
        _resource[`${name}`] = value;
        setResource(_resource);
    }

	const onGlobalFilterChange1 = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };
        _filters['global'].value = value;

        setFilters(_filters);
        setGlobalFilterValue1(value);
    }

	// TABLE COLUMN TEMPLATES (table provides rowdata and column info, can draw how needed)
	const shortTitleTemplate = (rowData, column) => {
        return (
			<div className="flex flex-column">
				<div className="resourceTitle">
					{rowData.short_title}
				</div>
				<div className="subtitle">
					{rowData.source}
				</div>

				{rowData.photo !== '' && 
					<div className="flex flex-column" >
						<div className="flex ">
							<img style={{width:'75px'}} src={NEURELIS_IMAGE_PATH + "resources/" + rowData.photo} />
						</div>
					</div>
				}
			</div>
		);
    }


	const categoryTemplate = (rowData, column) => {
       
		let strCategory = rowData.category
		if (categoryLookup[rowData.category]) strCategory = categoryLookup[rowData.category]
		return (
			<div className="flex flex-column">
				<div className="flex">
					<div>
						{strCategory}
					</div>
					
					{rowData.subcategory !== '' && 
						<>
							<div className="pl-1 pr-1">/</div>
							<div>{rowData.subcategory} {rowData.subcategoryYear} </div>
						</>
					}
				</div>
			</div>
		);
    }

	const pathTemplate = (rowData, column) => {
		if (rowData.fileType === 'file') {
			return (
				<div className="flex flex-column">
					<div className="resourceTitle">File</div>
					<div>
						{rowData.path}
					</div>
				</div>
			);
		} else if (rowData.fileType === 'url') {
			return (
				<div className="flex flex-column">
					<div className="resourceTitle">URL</div>
					<div>
						{rowData.url}
					</div>
				</div>
			);
		}
		
		
    }

	const lockedTemplate = (rowData, column) => {
        let iconLabel = <div  className="green">
			<i className="pi pi-lock-open" /> Unlocked
		</div>
		
		if (rowData.locked === 2) {
			iconLabel = <div className="red">
				<i className="pi pi-lock" /> Form Required
			</div>
		} else if (rowData.locked === 1) {
			iconLabel = <div>
				<i className="pi pi-lock" /> Locked
			</div>
		} 
		
		return (
			<div className="flex flex-column">
				<div className="resourceTitle">
					{iconLabel}
				</div>				
			</div>
		);
    }

	const activeTemplate = (rowData) => {
		if (rowData.isActive ===1 ) return <div className="green">Active</div>
		else return <div className="red">Inactive</div>
	 }

	const dateTemplate = (rowData) => {
		return (
			<div className="flex flex-column">
				<div>
					{dayjs(rowData.timeStamp).format('MM/DD/YYYY')}
				</div>
				<div>
					{dayjs(rowData.timeStamp).format('h:mm A')}
				</div>
			</div>
		)
	 }

    const actionBodyTemplate = (rowData) => {
        return (
            <React.Fragment>
                <Button icon="pi pi-pencil" className="p-button-rounded p-button-success mr-2" onClick={() => editResource(rowData)} />
                <Button icon="pi pi-trash" className="p-button-rounded p-button-warning" onClick={() => confirmDeleteResource(rowData)} />
            </React.Fragment>
        );
    }

	// OTHER TEMPLATES (JSX)
    const leftToolbarTemplate = () => {
        return <Button label="Add New Resource" icon="pi pi-plus" className="p-button-success mr-2" onClick={openNew} />
    }

	const rightToolbarTemplate = () => {
        return null
    }		

    const header = (
        <div className="table-header flex justify-content-between" >
            <h5 className="mx-0 my-1" >Manage Resources</h5>
			<div className="flex flex-column">
				<span className="p-input-icon-left">
					<i className="pi pi-search" />
					<InputText value={globalFilterValue1} onChange={onGlobalFilterChange1} placeholder="Search..." />
				</span>
			</div>
        </div>
    );

    const resourceDialogFooter = (
        <>
            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={hideDialog} />
            <Button label="Save" icon="pi pi-check" className="p-button-text" onClick={saveResource} />
        </>
    );

    const deleteResourceDialogFooter = (
        <>
            <Button label="No" icon="pi pi-times" className="p-button-text" onClick={hideDeleteResourceDialog} />
            <Button label="Yes" icon="pi pi-check" className="p-button-text" onClick={deleteResource} />
        </>
    );

	

	const statusRowFilterTemplate = (options) => {
		return (
            <Dropdown 
				value={options.value} 
				options={filterCategories} 
				onChange={(e) => {options.filterApplyCallback(e.value)}} 
				placeholder="Select Category" 
				className="p-column-filter" 
				showClear 
				style={{ minWidth: '14rem' }} />
        );
    };

	const imageHeaderTemplate = (options) => {
        const { className, chooseButton } = options;
        return (
            <div className={className} style={{ backgroundColor: 'transparent', display: 'flex', alignItems: 'center' }}>
                {chooseButton}
            </div>
        );
    };
    
    return (
        <div className="datatable-resources">
            <Toast ref={toast} />

            <div className="card" style={{width:"100%"}}>
                <Toolbar className="mb-4" left={leftToolbarTemplate} right={rightToolbarTemplate}></Toolbar>

                <DataTable ref={dt} value={resources} removableSort sortField="short_title" sortOrder={1}  
                    dataKey="resourceID" paginator rows={10} rowsPerPageOptions={[5, 10, 25]}
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} resources"
                    filters={filters} header={header} >
                   		<Column field="lastModified" sortable body={dateTemplate} header="Modified" style={{ width: '10%', verticalAlign:'top' }}></Column>
						<Column field="isActive" sortable body={activeTemplate} header="Active" style={{ width: '10%', verticalAlign:'top' }}></Column>
						<Column field="short_title" header="Short Title"sortable body={shortTitleTemplate} style={{ width: '25%', verticalAlign:'top' }}></Column>
						<Column field="category" header="Category" sortable body={categoryTemplate} style={{ minWidth: '12rem', verticalAlign:'top' }}
							filterField="category"  showFilterMatchModes={false}
							filter filterElement={statusRowFilterTemplate}
						/>
						<Column field="locked" sortable header="Lock Status" body={lockedTemplate} style={{ minWidth: '15%', verticalAlign:'top' }}></Column>
						<Column field="path" header="Link" body={pathTemplate} style={{ width: '25%', verticalAlign:'top',lineBreak: 'anywhere'}}></Column>
                  		<Column body={actionBodyTemplate} exportable={false} style={{ width: '10%', verticalAlign:'top' }}></Column>
				</DataTable>
            </div>

            <Dialog visible={resourceDialog} style={{ width: '850px' }} header="Resource Details" modal className="p-fluid" footer={resourceDialogFooter} onHide={hideDialog}>
                <div className="flex flex-column inputForm">
					<div className="inputFormFields">
						
						<div className="flex">
							<div className="field twoThirdField">
								<label htmlFor="name">Category</label>
								<Dropdown value={resource.category} 
									onChange={(e) => onInputChange(e.value, 'category')}
									options={categories} 
									className="w-full md:w-20rem" breakpoint="767px" placeholder="Select a Category"  
								/>
								{submitted && !resource.category && <small className="p-error">Category is required.</small>}
							</div>	
						</div>

						{resource.category === 'posters' && 
							<div className="flex">
								<div className="field halfField">
									<label htmlFor="isActive">Subcategory</label>
									<Dropdown value={resource.subcategory} onChange={(e) => onInputChange(e.value,'subcategory')} options={postersSubcategories}  
										placeholder="Select a Subcategory" className="w-full md:w-14rem" />
								</div>
								<div className="field halfField">
									<label htmlFor="name">Year</label>
									<Dropdown value={resource.subcategoryYear} onChange={(e) => onInputChange(e.value,'subcategoryYear')} options={subcatYears} 
										placeholder="Select a Year" className="w-full md:w-14rem" />
								</div>
							</div>
						}
						
						<div className="flex">
							<div className="field fullField">
								<label htmlFor="name">Title</label>
								<InputText id="name" value={resource.title} onChange={(e) => onInputChange(e.target.value, 'title')} required className={classNames({ 'p-invalid': submitted && !resource.title })} />
								{submitted && !resource.title && <small className="p-error">Title is required.</small>}
							</div>
						</div>
						<div className="flex">
							<div className="field fullField">
							<label htmlFor="name">Short Title</label>
								<InputText id="name" value={resource.short_title} onChange={(e) => onInputChange(e.target.value, 'short_title')} required />
								{submitted && !resource.short_title && <small className="p-error">Short Title is required.</small>}
							</div>
						</div>
						<div className="flex">
							<div className="field fullField">
								<label htmlFor="name">Source</label>
								<InputText id="name" value={resource.source} onChange={(e) => onInputChange(e.target.value, 'source')} required />
							</div>
						</div>

						<div className="flex">
							<div className="field fullField"  >
								<div className="flex justify-content-between" >
									<label htmlFor="photo">Photo (205 x 144) </label>
									{resource.resourceID > 0 && 
										<div className="eventMessage">
											(optional) Leave blank to keep current image
										</div>
									}
								</div>
								<FileUpload 
									name="photo" 
									ref={photoUploadRef} 
									accept="image/*" 
									maxFileSize={3000000} 
									headerTemplate={imageHeaderTemplate}
									emptyTemplate={<p className="m-0">Drag and drop optional photo to here to upload.</p>} />
								
							</div>
						</div>


						<div className="flex">
							<div className="field fullField">
								<label className="pr-2 " htmlFor="name">Link</label>
								<div className="flex mb-2">
									
									<FileTypeChooser 
										onInputChange={onInputChange}
										entity={resource}
										fileUploadRef={fileUploadRef}
										fileName="file"
										radioName="fileType"
										inputName="url"
										currentName="path"
										filePath={NEURELIS_PDF_PATH}
									/>
								</div>
							</div>
						</div>
						
						
						<div className="flex">
							<div className="field halfField">
								<label htmlFor="isActive">Status</label>
								<Dropdown value={resource.isActive} onChange={(e) => onInputChange(e.value,'isActive')} options={[{label: 'Active', value: 1},{label: 'Inactive',value: 0}]}  
									placeholder="Select a Status" className="w-full md:w-14rem" />
							</div>
							<div className="field halfField">
								<label htmlFor="name">Locked</label>
								<Dropdown value={resource.locked} onChange={(e) => onInputChange(e.value,'locked')} options={lockedStatus} optionLabel="name" 
									placeholder="Select a Locked Status" className="w-full md:w-14rem" />
							</div>
						</div>
						
						
					</div>
				</div>
            </Dialog>

            <Dialog visible={deleteResourceDialog} style={{ width: '450px' }} header="Confirm" modal footer={deleteResourceDialogFooter} onHide={hideDeleteResourceDialog}>
                <div className="confirmation-content">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem'}} />
                    {resource && <span>Are you sure you want to delete <b>{resource.title}</b>?</span>}
                </div>
            </Dialog>
        </div>
    );
}

export default ResourceTable;